/**
 * The browser's side of signing in with a mobile number.
 *
 * A Microsoft sign-in lives in MSAL's cache; a phone sign-in is one pass (a JWT
 * the server issued) plus who it belongs to, kept here. It has no silent
 * refresh: when it runs out, the person signs in again. This module is the one
 * place that knows whether a phone sign-in is active; authService and the app
 * shell ask it before driving MSAL.
 *
 * Kept in localStorage so a sign-in survives closing the tab, like skySchedule.
 * Every read and write is guarded: storage can be unavailable (private mode,
 * blocked site data), and then there is simply no phone sign-in.
 */

const STORAGE_KEY = "sky-phone-sign-in";
/** Where the phone sign-in is kept (for listening to other tabs' changes). */
export const PHONE_SESSION_KEY = STORAGE_KEY;
/** Stop using a pass this long before it runs out, so a request never lands on an expired one. */
const EXPIRY_MARGIN_MS = 60_000;

export interface PhoneSession {
  token: string;
  /** The id the server knows them by (their B2C id, else one it derives; see phoneUserId). */
  userId: string;
  email: string;
  name: string;
  /**
   * When the pass runs out (ms since epoch, by this device's clock): its
   * lifetime (exp - iat) from when it was kept, so a device clock that's off
   * neither ends a fresh pass nor keeps an old one.
   */
  expiresAt: number;
}

function claimsOf(token: string): { exp?: unknown; iat?: unknown; sub?: unknown } | null {
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    return JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/"))) as {
      exp?: unknown;
      iat?: unknown;
      sub?: unknown;
    };
  } catch {
    return null;
  }
}

function expiryOf(token: string): number | null {
  const claims = claimsOf(token);
  const exp = claims?.exp;
  if (typeof exp !== "number") return null;
  const iat = claims?.iat;
  return typeof iat === "number" && exp > iat ? Date.now() + (exp - iat) * 1000 : exp * 1000;
}

/** A stand-in id, only when the server didn't say one (sessions kept before it did). */
function fallbackUserId(token: string): string {
  const sub = claimsOf(token)?.sub;
  return typeof sub === "string" && sub ? `phone-user-${sub}` : "";
}

/**
 * Whether this tab runs on a phone sign-in, settled once when the app starts.
 * The phone sign-in is kept in localStorage, which every tab shares, while a
 * Microsoft sign-in belongs to its tab: a phone sign-in made in another tab
 * must never take over a tab signed in with Microsoft (it would act as
 * someone else while showing the first person). Unsettled (the sign-in page,
 * public pages) reads the kept phone sign-in as before.
 */
let tabUsesPhone: boolean | null = null;

/** Settle how this tab signed in (first call wins; a new sign-in reloads the page). */
export function settleTabSignIn(usesPhone: boolean): void {
  if (tabUsesPhone === null) tabUsesPhone = usesPhone;
}

/** For tests: forget how this tab signed in. */
export function __resetTabSignInForTests(): void {
  tabUsesPhone = null;
}

function read(): PhoneSession | null {
  if (tabUsesPhone === false) return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PhoneSession>;
    if (
      typeof parsed.token !== "string" ||
      typeof parsed.email !== "string" ||
      typeof parsed.expiresAt !== "number"
    ) {
      return null;
    }
    return {
      token: parsed.token,
      userId:
        typeof parsed.userId === "string" && parsed.userId
          ? parsed.userId
          : fallbackUserId(parsed.token),
      email: parsed.email,
      name: typeof parsed.name === "string" ? parsed.name : parsed.email,
      expiresAt: parsed.expiresAt,
    };
  } catch {
    return null;
  }
}

/** Keep a new phone sign-in. Returns false when the pass is unreadable or storage refuses it. */
export function savePhoneSession(
  token: string,
  user: { email: string; name?: string | null; userId?: string | null },
): boolean {
  const expiresAt = expiryOf(token);
  if (!expiresAt || !user.email) return false;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        token,
        userId: user.userId || fallbackUserId(token),
        email: user.email,
        name: user.name || user.email,
        expiresAt,
      }),
    );
    return true;
  } catch {
    return false;
  }
}

/** The phone sign-in, when there is one that hasn't run out. */
export function activePhoneSession(): PhoneSession | null {
  const session = read();
  if (!session) return null;
  return Date.now() < session.expiresAt - EXPIRY_MARGIN_MS ? session : null;
}

/** Whether a phone sign-in was kept at all, live or not (an ended one needs clearing). */
export function hasPhoneSession(): boolean {
  return read() !== null;
}

export function clearPhoneSession(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing kept, nothing to clear.
  }
}
